import React from 'react'
import { Box, FormLabel, MenuItem, TextField } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"
import { useUsers } from '@crm-src/hooks/useUsers'
import { Loader2, Users } from 'lucide-react'

interface UserSelectorProps {
  value?: string
  onValueChange: (value: string | undefined) => void
  label?: string
  placeholder?: string
  required?: boolean
  sx?: SxProps<Theme>
}

export function UserSelector({
  value,
  onValueChange,
  label = "Assigned Salesperson",
  placeholder = "Select a salesperson...",
  required = false,
  sx
}: UserSelectorProps) {
  const { users, loading, error } = useUsers()

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "unassigned") {
      onValueChange(undefined)
    } else {
      onValueChange(selectedValue)
    }
  }

  // Get the display name for the selected value
  const getDisplayName = () => {
    if (!value) return null
    const selectedUser = users.find(u => String(u.id) === String(value))
    return selectedUser ? selectedUser.full_name : null
  }

  return (
    <Box sx={[{ display: "flex", flexDirection: "column", gap: 1 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {label && (
        <FormLabel htmlFor="user-selector" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          {label}
          {required && <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>*</Box>}
        </FormLabel>
      )}

      <TextField
        id="user-selector"
        select
        size="small"
        fullWidth
        value={value ? String(value) : "unassigned"}
        onChange={(e) => handleValueChange(e.target.value)}
        disabled={loading}
        SelectProps={{
          displayEmpty: true,
          renderValue: () => {
            const name = getDisplayName()
            if (name) return name
            if (value) return null
            return <Box component="span" sx={{ color: "text.disabled" }}>{loading ? "Loading users..." : placeholder}</Box>
          },
        }}
      >
        {loading ? (
          <MenuItem value="loading" disabled>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="span"
                sx={{ display: "inline-flex", mr: 1, animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}
              >
                <Loader2 size={16} />
              </Box>
              Loading users...
            </Box>
          </MenuItem>
        ) : error ? (
          <MenuItem value="error" disabled>
            <Box sx={{ display: "flex", alignItems: "center", color: "error.main" }}>
              <Users size={16} style={{ marginRight: 8 }} />
              Error loading users
            </Box>
          </MenuItem>
        ) : (
          [
            <MenuItem key="unassigned" value="unassigned">
              <Box component="span" sx={{ color: "text.secondary", fontStyle: "italic" }}>Unassigned</Box>
            </MenuItem>,
            ...users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main", mr: 1 }} />
                  {user.full_name}
                </Box>
              </MenuItem>
            )),
          ]
        )}
      </TextField>
    </Box>
  )
}
